import { hashString128 } from '@w/utils'

// Grouping tiers: first-class names -> all names ("system variant") ->
// resolved source locations -> custom/message. First-class = our code
// including infra libs (we debug those, they are first-class citizens);
// foreign = node internals + 3rd-party deps, excluded BY IDENTITY so their
// position/presence never affects the hash. Split-biased on purpose: a split
// group is recoverable noise, a merged group hides a bug.
// Sentry hashes raw minified names when resolution fails and pays for it
// (per-deploy group splits, hard-coded ChunkLoadError buckets). We control
// the full chain - instrumentation, builds, funcmaps - so we are stricter:
// a name either resolved from our own source maps or it does not exist.
// messageFallback applies only when no frame tier produced input (callers pass
// it together with frames; it is the message-tier fallback, not an override).
export function computeFingerprint(opts: {
  errorType: string
  errorCode?: string
  resolvedFrames: {
    name: string | null
    file?: string | null
    line?: number
    foreign?: true
    sourcePosition?: boolean
  }[]
  messageFallback?: string
}): string {
  const prefix = (tier: 'fc' | 'sys' | 'loc' | 'msg') =>
    opts.errorCode
      ? `${opts.errorType}|${tier}|code:${opts.errorCode}`
      : `${opts.errorType}|${tier}`
  const firstClassNames = collectNames(
    opts.resolvedFrames.filter((frame) => !frame.foreign),
  )
  if (firstClassNames.length > 0) {
    return hashString128(`${prefix('fc')}|${firstClassNames.join('>')}`)
  }

  const systemNames = collectNames(opts.resolvedFrames)
  if (systemNames.length > 0) {
    return hashString128(`${prefix('sys')}|${systemNames.join('>')}`)
  }

  const locations = collectLocations(opts.resolvedFrames)
  if (locations.length > 0) {
    return hashString128(`${prefix('loc')}|${locations.join('>')}`)
  }

  return hashString128(`${prefix('msg')}|${opts.messageFallback ?? ''}`)
}

function collectNames(frames: { name: string | null }[]): string[] {
  const names: string[] = []
  for (const frame of frames) {
    if (!frame.name) continue
    if (names.length > 0 && names.at(-1) === frame.name) continue
    names.push(frame.name)
  }
  return names
}

function collectLocations(
  frames: { file?: string | null; line?: number; sourcePosition?: boolean }[],
): string[] {
  const locations: string[] = []
  for (const frame of frames) {
    // Funcmap-less apps intentionally fall through to raw generated file:line
    // for legacy parity; foreign frames explicitly set sourcePosition:false.
    if (frame.sourcePosition === false) continue
    if (!frame.file || frame.line === undefined) continue
    const location = `${frame.file}:${frame.line}`
    if (locations.length > 0 && locations.at(-1) === location) continue
    locations.push(location)
  }
  return locations
}
